import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { BrandliveEvent, ColorOptions } from '../../types/working-model';
import { useTypedSelector } from '../../store/reducers/use-typed-selector';


export function parseColor(color: [string, number]): string {
	const [h, opacity] = color;
	const r = '0x' + h[1] + h[2];
	const g = '0x' + h[3] + h[4];
	const b = '0x' + h[5] + h[6];

	return `rgba(${parseInt(r, 16)},${parseInt(g, 16)},${parseInt(
		b,
		16
	)},${opacity})`;
}
function parseColorRGB(color: [string, number]): string {
	const [h] = color;
	const r = '0x' + h[1] + h[2];
	const g = '0x' + h[3] + h[4];
	const b = '0x' + h[5] + h[6];

	return `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}`;

}

const defaultColors: ColorOptions = {
	customColor0: ['#000000', 0.5],
	accentColor: ['#000000', 0.5],
	headingTextColor: ['#000000', 0.5],
	backgroundColor: ['#000000', 0.5],
	bodyTextColor: ['#000000', 0.5],
	containerColor: ['#000000', 0.5],
	secondaryAccentColor: ['#000000', 0.5],
	secondaryBackgroundColor: ['#000000', 0.5]
};

//Export this helper functions so that live-event-document-head.tsx has access and the classOverrides and colorVariables only have to be edited in one place
export function getVariablesAndOverrides(event: BrandliveEvent | null): {
	colorVariables: string;
	classOverrides: string;
} {
	return useMemo(() => {

		const colors = event?.settings?.design?.colors?.colors ?? defaultColors;

		const _colorVariables: string[] = [];
		const _classOverrides: string[] = [];

		Object.entries(colors).forEach(([name, color]) => {
			if (!color) { return; } // Short curcuit if color is falsy
			const parsedColor = parseColor(color);
			_colorVariables.push(`--${name}: ${parsedColor};`);
			_colorVariables.push(`--${name}-rgb: ${parseColorRGB(color)};`);
			//The !important tag needs to stay here.
			//This is the finest detail of customization we can go to so they need to overide everything else. 
			//!important tags should be avoided everywhere else (if at all possible) so that these classes maintain their override power.
			_classOverrides.push(`
				.${name}-background { background-color: ${parsedColor} !important;}
				.${name}-border { border: 1px solid ${parsedColor} !important;}
				.${name}-color, .${name}-color>path, .${name}-color * { color: ${parsedColor} !important; fill: ${parsedColor} !important}
				.${name}-hover:hover { color: rgba(${parseColorRGB(color)}, .5) !important }
			`);
		});


		return {
			colorVariables: _colorVariables.join(' '),
			classOverrides: _classOverrides.join(' ')
		};
	}, [event]);
}


export default function DocumentHead(): JSX.Element {
	const panelOpen = useTypedSelector(state => state.EventPanelReducer.panelOpen);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);

	// centering from the left depending on if there is a panel or not (.5 view width - .5 editor width - .5 left nav width - left padding)
	// panel is 360px in total so you subtract an additional 180px when its open

	//desktop width is 1440, tablet 768, mobile 375. Half these values for the width map to help center properly;
	const widthMap = {
		desktop: "720px",
		tablet: "384px",
		mobile: "187.5px"
	};
	const centeredEditorMargin = panelOpen
		? `margin-left: calc(50vw - ${widthMap[editorSize]} - 50px - 25px - 180px);`
		: `margin-left: calc(50vw - ${widthMap[editorSize]} - 50px - 25px);`;

	/**
	 *
	 *
	 * What's with this hot mess you ask?
	 * Well, I'll tell you.
	 *
	 * Google fonts using the @import rule send in a stylesheet, not a .ttf file or something
	 * Custom uploaded fonts use the @font-face rule
	 *
	 * We want to use Google fonts for the licensing benefits, but we also want to let them upload their own fonts
	 * So we have to use a two-hander here. If it's a google font, make an import statement. Otherwise,
	 * make a font-face rule. Easy peasy.
	 *
	 */

	const { colorVariables, classOverrides } = getVariablesAndOverrides(workingEvent);

	return (
		<Helmet>
			<title>{'Brandlive'}</title>
			{workingEvent?.settings.design.font?.heading_url ? (
				<style>
					{`
					${workingEvent?.settings.design.font.heading_url.includes(
						'fonts.googleapis.com'
					)
							? `	
							@import url(${workingEvent?.settings.design.font.heading_url});`
							: `
							@font-face {
								font-family: "${workingEvent?.settings.design.font.heading_name}";
								src: url(${workingEvent?.settings.design.font.heading_url});
							}
						`
						}
						
					${workingEvent?.settings.design.font.body_url.includes(
							'fonts.googleapis.com'
						)
							? `
							@import url(${workingEvent?.settings.design.font.body_url});`
							: `
							@font-face {
								font-family: "${workingEvent?.settings.design.font.body_name}";
								src: url(${workingEvent?.settings.design.font.body_url});
							}
						`
						}
					
						.editor,
						.session-editor {
							--headingFont: "${workingEvent?.settings.design.font.heading_name}", sans-serif;
							--bodyFont: "${workingEvent?.settings.design.font.body_name}", sans-serif;
							${colorVariables}
							
							${centeredEditorMargin}
						}
						${classOverrides}
						.left-align-override {
							text-align: left;
							align-self: flex-start;
						} 
						.center-align-override{
							text-align: center;
							align-self: center;
						}
						.right-align-override {
							text-align: right;
							align-self: flex-end;
						}
					`}


				</style>
			) : (
				<style>
					{`@import url('https://fonts.googleapis.com/css?family=Open+Sans:300,300i,400,400i,600,600i,700,700i,800,800i');
						@import url('https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap');
					
						.editor {
							--headingFont: 'Roboto', sans-serif;
							--bodyFont: 'Open Sans', sans-serif; 
						}
					`}
				</style>
			)}

			{workingEvent?.custom_css && (
				<style>
					{workingEvent.custom_css}
				</style>
			)}

			{workingSession?.custom_css && (
				<style>
					{workingSession.custom_css}
				</style>
			)}
		</Helmet>
	);
}
